'use client';

import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Shield, Email, Phone, Info } from '@mui/icons-material';
import { Helmet } from 'react-helmet';

/**
 * 개인정보처리방침 페이지
 * 로그인 없이 접근 가능한 공개 페이지
 */
const PrivacyPolicy: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width:600px)');

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: isMobile ? 2 : 4 }}>
       <Helmet>
        <title>Zero Salon Business 개인정보처리방침</title>
        <meta property="og:title" content="Zero Salon Business 개인정보처리방침" />
        <meta property="og:description" content="Zero Salon Business 서비스 이용자를 위한 개인정보처리방침 안내입니다." />
        <meta property="og:image" content="https://test-zerosalon-branch.vercel.app/assets/privacy-og.png" />
        <meta property="og:url" content="https://test-zerosalon-branch.vercel.app/privacy-policy" />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="Zero Salon Business" />
        <meta property="og:locale" content="ko_KR" />
      </Helmet>
      <Container maxWidth="md">
        {/* 헤더 */}
        <Paper 
          elevation={3} 
          sx={{ 
            mb: isMobile ? 2 : 4, 
            borderRadius: isMobile ? 2 : 3,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            overflow: 'hidden'
          }}
        >
          <Box sx={{ p: isMobile ? 3 : 4, textAlign: 'center' }}>
            <Shield sx={{ fontSize: isMobile ? 40 : 48, mb: 2 }} />
            <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight="700" gutterBottom>
              개인정보처리방침
            </Typography>
            <Typography variant={isMobile ? 'body2' : 'body1'} sx={{ opacity: 0.9 }}>
              회사명 ( COMPANY NAME): 주식회사 라이토즈
            </Typography>
            <Typography variant={isMobile ? 'body2' : 'body1'} sx={{ opacity: 0.9 }}>
              앱 이름 ( APP NAME): Zero Salon Biz
            </Typography>
            <Typography variant={isMobile ? 'body2' : 'body1'} sx={{ opacity: 0.9 }}>
              연락처 ( CONTACT): help@zerosalon.com
            </Typography>
            <Typography variant={isMobile ? 'caption' : 'body2'} sx={{ opacity: 0.8, mt: 1 }}>
              시행일자: 2025년 7월 15일
            </Typography>
          </Box>
        </Paper>

        {/* 서문 */}
        <Paper elevation={2} sx={{ mb: isMobile ? 2 : 3, borderRadius: isMobile ? 2 : 3 }}>
          <Box sx={{ p: isMobile ? 2 : 3 }}>
            <Typography variant={isMobile ? 'body2' : 'body1'} sx={{ lineHeight: 1.7, color: 'text.secondary' }}>
              회사는 이용자의 개인정보를 보호하고, 관련 법령을 준수하기 위해 본 개인정보처리방침을 마련하였습니다. 
              본 방침은 이용자가 회사의 서비스(Zero Salon Biz 앱, 이하 &apos;서비스&apos;)를 이용할 때 회사가 처리하는 개인정보의 항목, 목적, 보유 기간 등을 안내합니다. 
              본 방침은 관계법령의 변경이나 회사의 정책 변경에 따라 수시로 개정될 수 있으며, 개정 시 앱 내 공지사항이나 이메일로 고지합니다.
            </Typography>
          </Box>
        </Paper>

        {/* 1. 처리하는 개인정보 항목 */}
        <Paper elevation={2} sx={{ mb: isMobile ? 2 : 3, borderRadius: isMobile ? 2 : 3 }}>
          <Box sx={{ p: isMobile ? 2 : 3 }}>
            <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight="600" gutterBottom color="primary">
              1. 처리하는 개인정보 항목
            </Typography>
            <Typography variant={isMobile ? 'body2' : 'body1'} sx={{ mb: 2, lineHeight: 1.6 }}>
              회사는 서비스 제공을 위해 필요한 최소한의 개인정보를 수집합니다. 수집 항목은 필수 정보(서비스 본질적 기능 수행을 위한 정보)와 선택 정보(추가 기능 제공을 위한 정보)로 구분됩니다.
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Typography variant={isMobile ? 'subtitle2' : 'subtitle1'} fontWeight="600" gutterBottom color="success.main">
                필수 정보:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText 
                    primary="회원 가입 및 로그인" 
                    secondary="이메일 주소, 비밀번호, 사용자 ID, Meta(Facebook) 로그인 정보(이름, 프로필 사진, 이메일 등 Meta 플랫폼에서 제공하는 데이터)"
                    primaryTypographyProps={{ fontSize: isMobile ? '0.875rem' : '1rem', fontWeight: 500 }}
                    secondaryTypographyProps={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="예약 및 서비스 이용" 
                    secondary="이름, 연락처(전화번호)"
                    primaryTypographyProps={{ fontSize: isMobile ? '0.875rem' : '1rem', fontWeight: 500 }}
                    secondaryTypographyProps={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="결제 정보" 
                    secondary="카드 번호(암호화 저장), 결제 내역(제3자 결제 대행사 위탁)"
                    primaryTypographyProps={{ fontSize: isMobile ? '0.875rem' : '1rem', fontWeight: 500 }}
                    secondaryTypographyProps={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
                  />
                </ListItem>
              </List>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant={isMobile ? 'subtitle2' : 'subtitle1'} fontWeight="600" gutterBottom color="info.main">
                선택 정보:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText 
                    primary="프로필 설정" 
                    secondary="닉네임, 프로필 사진, 선호 스타일 정보"
                    primaryTypographyProps={{ fontSize: isMobile ? '0.875rem' : '1rem', fontWeight: 500 }}
                    secondaryTypographyProps={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="마케팅 수신 동의" 
                    secondary="이벤트 참여를 위한 추가 연락처"
                    primaryTypographyProps={{ fontSize: isMobile ? '0.875rem' : '1rem', fontWeight: 500 }}
                    secondaryTypographyProps={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="자동 수집 정보" 
                    secondary="기기 정보(IP 주소, OS 버전, 앱 버전), 이용 기록(로그 데이터, 쿠키)"
                    primaryTypographyProps={{ fontSize: isMobile ? '0.875rem' : '1rem', fontWeight: 500 }}
                    secondaryTypographyProps={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
                  />
                </ListItem>
              </List>
            </Box>

            <Box sx={{ p: isMobile ? 1.5 : 2, bgcolor: 'info.50', borderRadius: 2, border: '1px solid', borderColor: 'info.200' }}>
              <Typography variant={isMobile ? 'caption' : 'body2'} color="info.main">
                <strong> Zero Salon Biz:</strong> Meta 플랫폼 데이터를 사용하는 경우(예: Facebook 로그인), Zero Salon Biz 앱 내에서 Meta의 데이터 정책을 준수하며, 해당 데이터는 이용자 동의 하에 수집됩니다.
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* 2. 개인정보의 처리 목적 */}
        <Paper elevation={2} sx={{ mb: isMobile ? 2 : 3, borderRadius: isMobile ? 2 : 3 }}>
          <Box sx={{ p: isMobile ? 2 : 3 }}>
            <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight="600" gutterBottom color="primary">
              2. 개인정보의 처리 목적
            </Typography>
            <Typography variant={isMobile ? 'body2' : 'body1'} sx={{ mb: 2, lineHeight: 1.6 }}>
              회사는 수집한 개인정보를 다음 목적으로 처리합니다. 목적 외 이용 시 이용자 동의를 재획득합니다.
            </Typography>

            <List>
              <ListItem>
                <ListItemText 
                  primary="회원 관리" 
                  secondary="가입 확인, 본인 인증, 부정 이용 방지"
                  primaryTypographyProps={{ fontSize: isMobile ? '0.875rem' : '1rem', fontWeight: 500 }}
                  secondaryTypographyProps={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="서비스 제공" 
                  secondary="결제 처리"
                  primaryTypographyProps={{ fontSize: isMobile ? '0.875rem' : '1rem', fontWeight: 500 }}
                  secondaryTypographyProps={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="마케팅 및 광고" 
                  secondary="이벤트 안내, 프로모션(선택 동의 시)"
                  primaryTypographyProps={{ fontSize: isMobile ? '0.875rem' : '1rem', fontWeight: 500 }}
                  secondaryTypographyProps={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="서비스 개선" 
                  secondary="이용 통계 분석, 오류 수정"
                  primaryTypographyProps={{ fontSize: isMobile ? '0.875rem' : '1rem', fontWeight: 500 }}
                  secondaryTypographyProps={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="법적 의무 이행" 
                  secondary="분쟁 해결, 법령 준수"
                  primaryTypographyProps={{ fontSize: isMobile ? '0.875rem' : '1rem', fontWeight: 500 }}
                  secondaryTypographyProps={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
                />
              </ListItem>
            </List>

                         <Box sx={{ p: isMobile ? 1.5 : 2, bgcolor: 'warning.50', borderRadius: 2, border: '1px solid', borderColor: 'warning.200' }}>
               <Typography variant={isMobile ? 'caption' : 'body2'} color="warning.main">
                 <strong> Zero Salon Biz:</strong> Meta 데이터(예: Facebook 프로필)는 Zero Salon Biz 앱 내에서 계정 연동 및 소셜 기능 제공을 위해 사용되며, Meta 플랫폼 약관에 따라 제3자와 공유되지 않습니다.
               </Typography>
             </Box>
          </Box>
        </Paper>

        {/* 3. 개인정보의 처리 및 보유 기간 */}
        <Paper elevation={2} sx={{ mb: isMobile ? 2 : 3, borderRadius: isMobile ? 2 : 3 }}>
          <Box sx={{ p: isMobile ? 2 : 3 }}>
            <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight="600" gutterBottom color="primary">
              3. 개인정보의 처리 및 보유 기간
            </Typography>
            <Typography variant={isMobile ? 'body2' : 'body1'} sx={{ mb: 2, lineHeight: 1.6 }}>
              <strong>원칙:</strong> 이용 목적이 달성되거나 이용자가 탈퇴 시 지체 없이 파기합니다.
            </Typography>

            <Typography variant={isMobile ? 'subtitle2' : 'subtitle1'} fontWeight="600" gutterBottom color="secondary.main">
              보유 기간 예시:
            </Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="회원 정보" 
                  secondary="탈퇴 후 30일 이내 파기(부정 이용 방지를 위한 별도 보관 가능)"
                  primaryTypographyProps={{ fontSize: isMobile ? '0.875rem' : '1rem', fontWeight: 500 }}
                  secondaryTypographyProps={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="결제 기록" 
                  secondary="전자상거래법에 따라 5년 보관"
                  primaryTypographyProps={{ fontSize: isMobile ? '0.875rem' : '1rem', fontWeight: 500 }}
                  secondaryTypographyProps={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="이용 로그" 
                  secondary="통신비밀보호법에 따라 3개월 보관"
                  primaryTypographyProps={{ fontSize: isMobile ? '0.875rem' : '1rem', fontWeight: 500 }}
                  secondaryTypographyProps={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="이벤트 참여 정보" 
                  secondary="이벤트 종료 후 3개월 보관"
                  primaryTypographyProps={{ fontSize: isMobile ? '0.875rem' : '1rem', fontWeight: 500 }}
                  secondaryTypographyProps={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Meta 데이터" 
                  secondary="이용자 동의 철회 또는 탈퇴 시 즉시 파기"
                  primaryTypographyProps={{ fontSize: isMobile ? '0.875rem' : '1rem', fontWeight: 500 }}
                  secondaryTypographyProps={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
                />
              </ListItem>
            </List>
          </Box>
        </Paper>

        {/* 4. 개인정보의 제3자 제공 및 위탁 */}
        <Paper elevation={2} sx={{ mb: isMobile ? 2 : 3, borderRadius: isMobile ? 2 : 3 }}>
          <Box sx={{ p: isMobile ? 2 : 3 }}>
            <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight="600" gutterBottom color="primary">
              4. 개인정보의 제3자 제공 및 위탁
            </Typography>
            
            <Box sx={{ mb: 3 }}>
              <Typography variant={isMobile ? 'subtitle2' : 'subtitle1'} fontWeight="600" gutterBottom color="error.main">
                제3자 제공:
              </Typography>
              <Typography variant={isMobile ? 'body2' : 'body1'} sx={{ mb: 1, lineHeight: 1.6 }}>
                이용자 동의 없이 제공하지 않습니다. 다만, 법령(예: 수사 기관 요청) 또는 긴급 상황(생명 보호) 시 예외.
              </Typography>
              <Typography variant={isMobile ? 'caption' : 'body2'} color="text.secondary">
                제공 예: Meta 플랫폼 연동 시, Meta에 인증 데이터 제공(동의 기반)
              </Typography>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant={isMobile ? 'subtitle2' : 'subtitle1'} fontWeight="600" gutterBottom color="warning.main">
                업무 위탁:
              </Typography>
              <Typography variant={isMobile ? 'body2' : 'body1'} sx={{ mb: 1, lineHeight: 1.6 }}>
                서비스 운영을 위해 국내/국외 업체에 위탁할 수 있습니다. 위탁 시 계약서에 보호 조치를 명시하고 감독합니다.
              </Typography>
              <Typography variant={isMobile ? 'caption' : 'body2'} color="text.secondary">
                위탁 업체 예: 클라우드 호스팅(AWS, 국외 이전 동의 필요), 결제 대행(PG사), 분석 도구(Google Analytics)
              </Typography>
            </Box>

            <Box sx={{ p: isMobile ? 1.5 : 2, bgcolor: 'error.50', borderRadius: 2, border: '1px solid', borderColor: 'error.200' }}>
              <Typography variant={isMobile ? 'caption' : 'body2'} color="error.main">
                <strong>국외 이전 시:</strong> 이용자 동의 획득, 이전 국가/업체 공개(예: AWS - 미국, 표준보호조항 적용)
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* 5. 개인정보의 파기 */}
        <Paper elevation={2} sx={{ mb: isMobile ? 2 : 3, borderRadius: isMobile ? 2 : 3 }}>
          <Box sx={{ p: isMobile ? 2 : 3 }}>
            <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight="600" gutterBottom color="primary">
              5. 개인정보의 파기
            </Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="파기 절차" 
                  secondary="보유 기간 경과 시 개인정보 보호책임자 승인 후 파기"
                  primaryTypographyProps={{ fontSize: isMobile ? '0.875rem' : '1rem', fontWeight: 500 }}
                  secondaryTypographyProps={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="파기 방법" 
                  secondary="전자 파일은 복구 불가능한 방식으로 삭제, 종이 문서는 분쇄 또는 소각"
                  primaryTypographyProps={{ fontSize: isMobile ? '0.875rem' : '1rem', fontWeight: 500 }}
                  secondaryTypographyProps={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
                />
              </ListItem>
            </List>
            <Typography variant={isMobile ? 'caption' : 'body2'} color="text.secondary">
              법령에 따라 별도 보관 시, 다른 개인정보와 분리 저장.
            </Typography>
          </Box>
        </Paper>

        {/* 6. 이용자와 법정대리인의 권리·의무 및 행사 방법 */}
        <Paper elevation={2} sx={{ mb: isMobile ? 2 : 3, borderRadius: isMobile ? 2 : 3 }}>
          <Box sx={{ p: isMobile ? 2 : 3 }}>
            <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight="600" gutterBottom color="primary">
              6. 이용자와 법정대리인의 권리·의무 및 행사 방법
            </Typography>
            
            <Box sx={{ mb: 3 }}>
              <Typography variant={isMobile ? 'subtitle2' : 'subtitle1'} fontWeight="600" gutterBottom color="success.main">
                권리:
              </Typography>
              <Typography variant={isMobile ? 'body2' : 'body1'} sx={{ mb: 1, lineHeight: 1.6 }}>
                개인정보 열람, 정정, 삭제, 처리 정지, 동의 철회
              </Typography>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant={isMobile ? 'subtitle2' : 'subtitle1'} fontWeight="600" gutterBottom color="info.main">
                행사 방법:
              </Typography>
              <Typography variant={isMobile ? 'body2' : 'body1'} sx={{ mb: 1, lineHeight: 1.6 }}>
                앱 내 &apos;설정 {'>'}개인정보 관리&apos; 메뉴, 이메일(help@zerosalon.co.kr)로 요청. 
                본인 확인 후 10일 이내 처리.
              </Typography>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant={isMobile ? 'subtitle2' : 'subtitle1'} fontWeight="600" gutterBottom color="warning.main">
                법정대리인:
              </Typography>
              <Typography variant={isMobile ? 'body2' : 'body1'} sx={{ mb: 1, lineHeight: 1.6 }}>
                미성년자(14세 미만) 보호자는 위임장 제출로 권리 행사 가능
              </Typography>
            </Box>

            <Box sx={{ p: isMobile ? 1.5 : 2, bgcolor: 'grey.100', borderRadius: 2 }}>
              <Typography variant={isMobile ? 'caption' : 'body2'} color="text.secondary">
                <strong>제한 사유:</strong> 법령상 의무 이행 시 제한될 수 있음<br/>
                <strong>의무:</strong> 이용자는 타인의 개인정보를 침해하지 않을 의무가 있습니다.
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* 7. 개인정보 자동 수집 장치의 설치·운영 및 거부 */}
        <Paper elevation={2} sx={{ mb: isMobile ? 2 : 3, borderRadius: isMobile ? 2 : 3 }}>
          <Box sx={{ p: isMobile ? 2 : 3 }}>
            <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight="600" gutterBottom color="primary">
              7. 개인정보 자동 수집 장치의 설치·운영 및 거부
            </Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="쿠키 등 사용" 
                  secondary="서비스 이용 편의를 위해 쿠키, 웹 비콘 사용(이용자 행동 분석)"
                  primaryTypographyProps={{ fontSize: isMobile ? '0.875rem' : '1rem', fontWeight: 500 }}
                  secondaryTypographyProps={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="거부 방법" 
                  secondary="브라우저 설정에서 쿠키 차단 가능. 차단 시 일부 서비스(맞춤 추천) 제한될 수 있음"
                  primaryTypographyProps={{ fontSize: isMobile ? '0.875rem' : '1rem', fontWeight: 500 }}
                  secondaryTypographyProps={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
                />
              </ListItem>
            </List>
          </Box>
        </Paper>

        {/* 8. 개인정보의 안전성 확보 조치 */}
        <Paper elevation={2} sx={{ mb: isMobile ? 2 : 3, borderRadius: isMobile ? 2 : 3 }}>
          <Box sx={{ p: isMobile ? 2 : 3 }}>
            <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight="600" gutterBottom color="primary">
              8. 개인정보의 안전성 확보 조치
            </Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="관리적 조치" 
                  secondary="내부 관리 계획 수립, 직원 교육, 정기 점검"
                  primaryTypographyProps={{ fontSize: isMobile ? '0.875rem' : '1rem', fontWeight: 500 }}
                  secondaryTypographyProps={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="기술적 조치" 
                  secondary="암호화(비밀번호 해시), 접근 통제, 백신 프로그램"
                  primaryTypographyProps={{ fontSize: isMobile ? '0.875rem' : '1rem', fontWeight: 500 }}
                  secondaryTypographyProps={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="물리적 조치" 
                  secondary="서버실 잠금, 문서 보관 제한"
                  primaryTypographyProps={{ fontSize: isMobile ? '0.875rem' : '1rem', fontWeight: 500 }}
                  secondaryTypographyProps={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
                />
              </ListItem>
            </List>
            <Box sx={{ p: isMobile ? 1.5 : 2, bgcolor: 'success.50', borderRadius: 2, border: '1px solid', borderColor: 'success.200' }}>
              <Typography variant={isMobile ? 'caption' : 'body2'} color="success.main">
                <strong>Meta 데이터 보호:</strong> Meta 플랫폼 약관 준수, 암호화 전송
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* 9. 개인정보 보호책임자 및 문의처 */}
        <Paper elevation={2} sx={{ mb: isMobile ? 2 : 3, borderRadius: isMobile ? 2 : 3 }}>
          <Box sx={{ p: isMobile ? 2 : 3 }}>
            <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight="600" gutterBottom color="primary">
              9. 개인정보 보호책임자 및 문의처
            </Typography>
            
            <Box sx={{ mb: 3, p: isMobile ? 2 : 3, bgcolor: 'primary.50', borderRadius: 2, border: '1px solid', borderColor: 'primary.200' }}>
              <Typography variant={isMobile ? 'subtitle2' : 'subtitle1'} fontWeight="600" gutterBottom color="primary.main">
                개인정보 보호책임자: 안승현
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Email sx={{ fontSize: isMobile ? 16 : 18, color: 'primary.main' }} />
                <Typography variant={isMobile ? 'body2' : 'body1'}>help@zerosalon.com</Typography>
              </Box>
            </Box>

            <Typography variant={isMobile ? 'body2' : 'body1'} sx={{ mb: 2, lineHeight: 1.6 }}>
              고충 처리 시 위 연락처로 접수. 처리 결과는 10일 이내 통보.
            </Typography>

            <Box sx={{ p: isMobile ? 1.5 : 2, bgcolor: 'info.50', borderRadius: 2, border: '1px solid', borderColor: 'info.200' }}>
              <Typography variant={isMobile ? 'caption' : 'body2'} color="info.main">
                <strong>관련 기관:</strong> 개인정보보호위원회(www.privacy.go.kr) 또는 KISA(www.kisa.or.kr)에 신고 가능
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* 10. Meta 플랫폼 데이터 처리 */}
        <Paper elevation={2} sx={{ mb: isMobile ? 2 : 3, borderRadius: isMobile ? 2 : 3 }}>
          <Box sx={{ p: isMobile ? 2 : 3 }}>
            <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight="600" gutterBottom color="primary">
              10. Meta 플랫폼 데이터 처리
            </Typography>
                         <Typography variant={isMobile ? 'body2' : 'body1'} sx={{ mb: 2, lineHeight: 1.6 }}>
               회사는 Zero Salon Biz 앱에서 Facebook 로그인 등 Meta 플랫폼 기능을 사용할 때 Meta의 개인정보 정책을 준수합니다. 
               Meta로부터 받은 데이터(이름, 이메일 등)는 이용자 동의 하에 Zero Salon Biz 앱의 서비스 제공 목적으로만 사용하며, 제3자와 공유하지 않습니다.
             </Typography>
            <Box sx={{ p: isMobile ? 1.5 : 2, bgcolor: 'warning.50', borderRadius: 2, border: '1px solid', borderColor: 'warning.200' }}>
              <Typography variant={isMobile ? 'caption' : 'body2'} color="warning.main">
                자세한 내용은 Meta 개인정보처리방침을 참조하세요. (facebook.com)
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* 11. 개인정보처리방침의 변경 */}
        <Paper elevation={2} sx={{ mb: isMobile ? 2 : 4, borderRadius: isMobile ? 2 : 3 }}>
          <Box sx={{ p: isMobile ? 2 : 3 }}>
            <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight="600" gutterBottom color="primary">
              11. 개인정보처리방침의 변경
            </Typography>
            <Typography variant={isMobile ? 'body2' : 'body1'} sx={{ lineHeight: 1.6 }}>
              본 방침 변경 시 최소 7일 전 앱 내 공지사항에 게시합니다. 
              중요한 변경(수집 항목 추가 등)은 이용자 동의를 재획득합니다.
            </Typography>
          </Box>
        </Paper>

        {/* 푸터 */}
        <Box sx={{ textAlign: 'center', py: isMobile ? 2 : 3 }}>
          <Typography variant={isMobile ? 'caption' : 'body2'} color="text.secondary">
            © 2025 라이토즈 주식회사. 모든 권리 보유.
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            개인정보 관련 문의: help@zerosalon.com
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default PrivacyPolicy; 